import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppProviders";
import { ORDERS_API_URL, PORTAL_ADDON, PAYMENTS_ENABLED, SUPPORT_EMAIL } from "@/config";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";

const parseEuro = (str) => {
  if (!str) return 0;
  const cleaned = str.replace(/[^\d,.-]/g, "").replace(/\.(?=\d{3})/g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
};

const fmt = (n) =>
  "€" + n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Checkout = () => {
  const { t, setTermsOpen } = useApp();
  const s = t.shop;
  const p = t.pricing;
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const planId = params.get("plan") || "professional";
  const isPortalOnly = planId === "portal";
  const billing = isPortalOnly ? "annual" : params.get("billing") || "annual";
  const portal = !isPortalOnly && params.get("portal") === "1";
  const tier = isPortalOnly ? null : p.tiers.find((x) => x.id === planId) || p.tiers[0];

  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [kvk, setKvk] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const base = isPortalOnly ? 0 : parseEuro(tier.prices[billing]);
  const addon = portal ? PORTAL_ADDON.annual : 0;
  const portalBase = isPortalOnly ? PORTAL_ADDON.annual : 0;
  const subtotal = base + addon + portalBase;
  const vat = subtotal * 0.21;
  const total = subtotal + vat;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) return toast.error(s.emailRequired);
    if (!agreed) return toast.error(s.termsRequired);

    if (!PAYMENTS_ENABLED) {
      const productLine = isPortalOnly
        ? `${t.portal.card.title} — ${s.portalYearly}`
        : `${tier.name} — ${p.billing[billing]}`;
      const lines = [
        s.mailIntro,
        "",
        productLine,
        ...(isPortalOnly
          ? []
          : [`${s.portalAddon}: ${portal ? "ja / yes" : "nee / no"}`]),
        `${s.subtotal}: ${fmt(subtotal)}`,
        `${s.vatLine}: ${fmt(vat)}`,
        `${s.total}: ${fmt(total)}`,
        "",
        `${s.emailLabel}: ${email}`,
        `${s.companyLabel}: ${company || "-"}`,
        `${s.kvkLabel}: ${kvk || "-"}`,
      ];
      const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        `${s.mailSubject} · ${isPortalOnly ? "Klantportaal" : tier.name}`
      )}&body=${encodeURIComponent(lines.join("\n"))}`;
      window.location.href = href;
      toast.success(s.mailSent);
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${ORDERS_API_URL}/api/checkout/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: planId,
          billing_mode: billing,
          portal_addon: portal,
          email,
          company_name: company,
          kvk: kvk || null,
        }),
      });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        navigate("/thank-you");
      }
    } catch (err) {
      toast.error(s.unavailable);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-body antialiased">
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link
            to="/shop"
            data-testid="checkout-back"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {s.back}
          </Link>
          <h1 className="font-heading mt-4 text-3xl sm:text-4xl font-bold tracking-tight">
            {s.checkoutTitle}
          </h1>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            {/* Form */}
            <form onSubmit={submit} className="space-y-5" data-testid="checkout-form">
              <div>
                <label className="text-sm font-medium">{s.emailLabel}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={s.emailPh}
                  data-testid="checkout-email"
                  className="mt-1.5 w-full rounded-sm border border-border bg-card px-3 py-2.5 text-sm outline-none focus:border-blue-600"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">{s.companyLabel}</label>
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder={s.companyPh}
                  data-testid="checkout-company"
                  className="mt-1.5 w-full rounded-sm border border-border bg-card px-3 py-2.5 text-sm outline-none focus:border-blue-600"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{s.kvkLabel}</label>
                <input
                  value={kvk}
                  onChange={(e) => setKvk(e.target.value)}
                  placeholder={s.kvkPh}
                  data-testid="checkout-kvk"
                  className="mt-1.5 w-full rounded-sm border border-border bg-card px-3 py-2.5 text-sm outline-none focus:border-blue-600"
                />
              </div>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="checkout-terms"
                  className="mt-0.5 h-4 w-4 accent-blue-600"
                />
                <span className="text-muted-foreground">
                  {s.termsPrefix}{" "}
                  <button
                    type="button"
                    onClick={() => setTermsOpen?.(true)}
                    className="text-blue-500 hover:underline"
                  >
                    {s.termsLink}
                  </button>
                </span>
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="checkout-pay"
                className="inline-flex h-11 w-full items-center justify-center rounded-sm bg-blue-600 px-5 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
              >
                {busy
                  ? s.processing
                  : PAYMENTS_ENABLED
                  ? `${s.pay} · ${fmt(total)}`
                  : `${s.orderEmail} · ${fmt(total)}`}
              </button>
            </form>

            {/* Summary */}
            <div
              data-testid="checkout-summary"
              className="rounded-sm border border-border bg-card p-6"
            >
              <h2 className="font-heading text-lg font-semibold">{s.summary}</h2>
              {isPortalOnly ? (
                <div className="mt-4 flex items-start justify-between text-sm" data-testid="checkout-portal-only">
                  <span>
                    {t.portal.card.title}
                    <span className="block text-xs text-muted-foreground">
                      {s.portalYearly}
                    </span>
                  </span>
                  <span className="font-mono">{fmt(portalBase)}</span>
                </div>
              ) : (
                <>
                  <div className="mt-4 flex items-start justify-between text-sm">
                    <span>
                      SecureMaintPro {tier.name}
                      <span className="block text-xs text-muted-foreground">
                        {p.billing[billing]}
                      </span>
                    </span>
                    <span className="font-mono">{fmt(base)}</span>
                  </div>
                  {portal && (
                    <div className="mt-3 flex items-start justify-between text-sm">
                      <span>
                        {s.portalAddon}
                        <span className="block text-xs text-muted-foreground">
                          {s.portalYearly}
                        </span>
                      </span>
                      <span className="font-mono">{fmt(addon)}</span>
                    </div>
                  )}
                </>
              )}
              <div className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{s.subtotal}</span>
                  <span className="font-mono">{fmt(subtotal)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{s.vatLine}</span>
                  <span className="font-mono">{fmt(vat)}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
                  <span>{s.total}</span>
                  <span className="font-mono text-blue-500" data-testid="checkout-total">
                    {fmt(total)}
                  </span>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground/70">
                {p.billing[billing]}
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Checkout;
